import { Fragment, useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Button } from "../ui/button";
import { adminApi } from "../../lib/authApi";
import { TaskStatusBadge, fmtConf, fmtMs, fmtTs } from "./agentShared";
import { EMPTY_FILTERS, TaskFilters } from "./TaskFilters";
import { TaskDetail } from "./TaskDetail";

const PAGE = 25;

export const RecentTasks = ({ tick, onOpenAgent, filters, onFiltersChange }) => {
  const setFilters = onFiltersChange;
  const [data, setData] = useState({ items: [], total: 0, agents: {}, task_types: [], scopes: [] });
  const [open, setOpen] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async (limit = PAGE) => {
    try {
      const params = Object.fromEntries(Object.entries({ ...filters, limit }).filter(([, v]) => v !== ""));
      const { data: d } = await adminApi.get("/admin/brain/tasks", { params });
      setData(d);
      setError("");
    } catch {
      setError("Recent tasks could not be loaded.");
    }
  }, [filters]);
  useEffect(() => { load(); }, [load, tick]);

  const agents = data.agents || {};
  const traceWorkflow = (id) => { setFilters({ ...EMPTY_FILTERS, workflow_id: id }); setOpen(null); };
  return (
    <div className="space-y-3" id="agent-tasks-section" data-testid="agent-tasks-section">
      <TaskFilters filters={filters} onChange={(f) => { setFilters(f); setOpen(null); }} agents={agents} taskTypes={data.task_types} scopes={data.scopes} />
      <p className="text-xs text-muted-foreground" data-testid="agent-tasks-count">{data.items.length} of {data.total} task records shown · newest first · click a row for the correlation trace</p>
      {error && <p className="text-xs text-destructive" data-testid="agent-tasks-error">{error}</p>}
      <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="agent-tasks-wrap">
        <Table data-testid="agent-tasks-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-6" /><TableHead>When</TableHead><TableHead>Agent</TableHead><TableHead>Task</TableHead><TableHead>Status</TableHead><TableHead>Memory</TableHead><TableHead>Confidence</TableHead><TableHead>Processing</TableHead><TableHead>Review</TableHead><TableHead>Scope</TableHead><TableHead>Workflow</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.length === 0 && <TableRow><TableCell colSpan={11} className="py-10 text-center text-sm text-muted-foreground" data-testid="agent-tasks-empty">No agent tasks match. Tasks appear when drawings are analyzed, approvals run, candidates are ranked, inspections start or documents are rendered.</TableCell></TableRow>}
            {data.items.map((t) => (
              <Fragment key={t.id}>
                <TableRow className="cursor-pointer" onClick={() => setOpen(open === t.id ? null : t.id)} data-testid={`agent-task-row-${t.id}`} aria-expanded={open === t.id}>
                  <TableCell className="px-2 text-muted-foreground">{open === t.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtTs(t.created_at)}</TableCell>
                  <TableCell className="whitespace-nowrap"><button type="button" onClick={(e) => { e.stopPropagation(); onOpenAgent(t.agent_id); }} className="text-sm font-medium text-primary hover:underline" data-testid={`agent-task-agent-${t.id}`}>{agents[t.agent_id] || t.agent_id || "—"}</button></TableCell>
                  <TableCell className="whitespace-nowrap font-mono-tech text-xs">{t.task_type}</TableCell>
                  <TableCell><TaskStatusBadge status={t.status} testId={`agent-task-status-${t.id}`} /></TableCell>
                  <TableCell className="text-xs" data-testid={`agent-task-memory-${t.id}`}>{t.memory_hit ? "hit" : t.dedup === "collapsed" ? "collapsed" : "miss"}</TableCell>
                  <TableCell className="font-mono-tech text-xs">{fmtConf(t.confidence)}</TableCell>
                  <TableCell className="font-mono-tech text-xs whitespace-nowrap">{fmtMs(t.processing_ms)}</TableCell>
                  <TableCell className="text-xs">{t.requires_review ? "required" : "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{(t.scope_key || "").split(":")[0]}{t.error ? <span className="ml-2 text-red-600 dark:text-red-400" data-testid={`agent-task-error-${t.id}`} title={t.error}>· {t.error_category || "error"}</span> : null}</TableCell>
                  <TableCell className="font-mono-tech text-[11px] text-muted-foreground whitespace-nowrap">{t.workflow_id ? <button type="button" className="hover:underline" onClick={(e) => { e.stopPropagation(); traceWorkflow(t.workflow_id); }} data-testid={`agent-task-workflow-${t.id}`}>{t.workflow_id.slice(0, 8)}…</button> : "—"}</TableCell>
                </TableRow>
                {open === t.id && <TableRow data-testid={`agent-task-detail-row-${t.id}`}><TableCell colSpan={11} className="p-2"><TaskDetail taskId={t.id} onTrace={setOpen} onFilterWorkflow={traceWorkflow} /></TableCell></TableRow>}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
      {data.items.length < data.total && <Button variant="outline" size="sm" className="rounded-full" onClick={() => load(Math.min(data.items.length + PAGE, 200))} data-testid="agent-tasks-more">Show more ({data.total - data.items.length} remaining)</Button>}
    </div>
  );
};
